/**
 * MTO (Made-to-Order) localStorage Cache System
 * Efficiently loads and caches metaobject translations
 */

(function() {
    'use strict';
    
    // Configuration
    var CACHE_KEY = 'mto_' + window.Shopify?.shop?.split('.')[0] || 'unknown' + '_v1';
    var CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
    var DEBUG = window.MTO_DEBUG || false;
    
    /**
     * Initialize MTO cache system
     */
    function initMtoCache() {
        // Ensure theme object exists
        window.theme = window.theme || {};
        
        // Initialize MTO lookup structure
        if (!theme.mtoLookup) {
            theme.mtoLookup = { byVariant: {} };
        }
        
        // Try to load from cache first
        if (loadFromCache()) {
            if (DEBUG) console.log('✅ MTO cache loaded from localStorage');
            return;
        }
        
        // Cache miss - need to build data from metaobjects
        if (DEBUG) console.log('⚠️ MTO cache miss - rebuilding from metaobjects');
        buildAndCacheMtoData();
    }
    
    /**
     * Load MTO data from localStorage cache
     * @returns {boolean} true if cache was valid and loaded
     */
    function loadFromCache() {
        try {
            var cached = JSON.parse(localStorage.getItem(CACHE_KEY));
            
            if (!cached || !cached.data || !cached.timestamp) {
                return false;
            }
            
            // Check if cache is still valid
            var now = Date.now();
            if (now - cached.timestamp > CACHE_DURATION) {
                if (DEBUG) console.log('📅 MTO cache expired');
                localStorage.removeItem(CACHE_KEY);
                return false;
            }
            
            // Load cached data into theme lookup
            theme.mtoLookup.byVariant = cached.data || {};
            
            return true;
            
        } catch (error) {
            if (DEBUG) console.error('❌ MTO cache load error:', error);
            localStorage.removeItem(CACHE_KEY);
            return false;
        }
    }
    
    /**
     * Build MTO data from metaobjects and cache it
     * This should be called once when cache is empty/invalid
     */
    function buildAndCacheMtoData() {
        try {
            // Get metaobjects data from Liquid (handles pagination)
            getMetaobjectsDataFromLiquid().then(function(metaobjectsData) {
                if (!metaobjectsData || metaobjectsData.length === 0) {
                    if (DEBUG) console.log('⚠️ No metaobjects data available');
                    return;
                }
                
                // Build lookup table
                var variantLookup = {};
                var processedCount = 0;
                
                metaobjectsData.forEach(function(mto) {
                    var translations = {
                        en: mto.text_in_english,
                        cs: mto.text_in_czech,
                        de: mto.text_in_german,
                        fr: mto.text_in_french
                    };
                    
                    // Process each variant in this metaobject
                    if (mto.product_list && mto.product_list.value) {
                        mto.product_list.value.forEach(function(variant) {
                            // Handle both GID and numeric ID formats
                            var variantId = variant.numeric_id || variant.id;
                            var gid = variant.gid || variant.id;
                            
                            if (variantId) {
                                variantLookup[String(variantId)] = translations;
                                processedCount++;
                                
                                // Also map GID if it's different and Shopify uses GID format
                                if (gid !== variantId && gid.includes('//shopify/')) {
                                    variantLookup[String(gid)] = translations;
                                    processedCount++;
                                }
                                
                                if (DEBUG && (variantId === '49597661217111' || gid === '49597661217111')) {
                                    console.log('🎯 Found target variant in metaobjects:', {
                                        metaobject: mto.product_identifier || 'unknown',
                                        numeric_id: variantId,
                                        gid: gid,
                                        translations: translations
                                    });
                                }
                            }
                        });
                    }
                });
                
                // Store in theme and cache
                theme.mtoLookup.byVariant = variantLookup;
                saveToCache(variantLookup);
                
                if (DEBUG) {
                    console.log('🎯 MTO cache built:', processedCount, 'variants processed');
                    console.log('📦 MTO translations loaded for', Object.keys(variantLookup).length, 'variants');
                    console.log('📊 Pagination complete - all', metaobjectsData.length, 'metaobjects loaded');
                    
                    // Check for target variant specifically
                    var targetFound = variantLookup['49597661217111'] || variantLookup['gid://shopify/ProductVariant/49597661217111'];
                    console.log('🎯 Target variant check:', !!targetFound ? 'FOUND!' : 'NOT FOUND');
                    
                    if (targetFound) {
                        console.log('✅ Target variant translations:', targetFound);
                    } else {
                        // Show Betty velvet variants
                        var bettyMTO = metaobjectsData.find(function(mto) {
                            return mto.product_identifier && mto.product_identifier.toLowerCase().includes('betty velvet');
                        });
                        
                        if (bettyMTO) {
                            console.log('🔍 Betty velvet sample variants:', bettyMTO.product_list.value.slice(0, 5).map(v => ({
                                numeric_id: v.numeric_id,
                                id: v.id,
                                gid: v.gid,
                                is_target: (v.numeric_id === '49597661217111' || v.id === '49597661217111')
                            })));
                        }
                    }
                }
                
                // Dispatch ready event
                document.dispatchEvent(new CustomEvent('mtoCacheReady'));
                
            }).catch(function(error) {
                if (DEBUG) console.error('❌ MTO cache build error:', error);
            });
            
        } catch (error) {
            if (DEBUG) console.error('❌ MTO cache build error:', error);
        }
    }
    
    /**
     * Save MTO data to localStorage cache
     * @param {Object} data - The data to cache
     */
    function saveToCache(data) {
        try {
            var cacheData = {
                data: data,
                timestamp: Date.now()
            };
            localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
            
            if (DEBUG) console.log('💾 MTO data cached successfully');
            
        } catch (error) {
            if (DEBUG) console.error('❌ MTO cache save error:', error);
        }
    }
    
    /**
     * Get metaobjects data from Liquid template
     * This would contain all metaobject data after pagination
     * @returns {Promise<Array>} Promise that resolves to array of metaobject data
     */
function getMetaobjectsDataFromLiquid() {
    return new Promise(function(resolve) {
        // Start with initial batch from Liquid
        var allMetaobjects = window.mtoMetaobjectsCache || [];
        var config = window.mtoMetaobjectsConfig || {};
        
        if (DEBUG) {
            console.log('🔍 MTO Pagination Info:', config);
            console.log('📦 Initial batch size:', allMetaobjects.length);
        }
        
        // Check if Shopify returned null totalCount (common issue)
        if (config.totalCount === null || config.needsManualCount) {
            if (DEBUG) {
                console.log('⚠️ Total count unavailable, attempting manual pagination discovery');
                console.log('🔄 Will try fetching additional pages to discover total');
            }
            
            // Try to discover more pages manually
            discoverAllMetaobjectPages(allMetaobjects, config).then(function(completeData) {
                resolve(completeData);
            });
            return;
        }
        
        // If we have proper count and all pages are loaded
        if (!config.hasMorePages && config.currentBatch >= config.totalCount) {
            if (DEBUG) console.log('✅ All pages loaded server-side');
            resolve(allMetaobjects);
            return;
        }
        
        // Otherwise, need to fetch additional pages via AJAX
        fetchAdditionalMetaobjectPages(allMetaobjects, config).then(function(completeData) {
            resolve(completeData);
        });
    });
}
    
    /**
     * Fetch additional metaobject pages via AJAX (for when we exceed Shopify limits)
     * @param {Array} currentData - Current metaobjects array
     * @param {Object} config - Pagination configuration
     * @returns {Promise<Array>} Complete array of all metaobjects
     */
function fetchAdditionalMetaobjectPages(currentData, config) {
    return new Promise(function(resolve) {
        // Skip AJAX if we have all data server-side
        if (config.totalCount && currentData.length >= config.totalCount) {
            if (DEBUG) console.log('✅ All metaobjects available server-side, no AJAX needed');
            resolve(currentData);
            return;
        }
        
        // Check if we need AJAX pagination
        if (config.totalCount && config.totalCount > 250) {
            if (DEBUG) {
                console.log('🔄 Need AJAX pagination - total:', config.totalCount, 'current:', currentData.length);
                console.log('⚠️ AJAX pagination requires server-side implementation');
            }
            
            // For now, work with what we have
            // In production, implement AJAX calls like:
            // fetch(`/metaobjects/made_to_order.json?limit=250&page=2`)
            // .then(response => response.json())
            // .then(data => mergeWithCurrentData(currentData, data))
        }
        
        resolve(currentData);
    });
}

function discoverAllMetaobjectPages(currentData, config) {
    return new Promise(function(resolve) {
        if (DEBUG) {
            console.log('🔍 Manual pagination discovery starting with', currentData.length, 'metaobjects');
        }
        
        // Strategy 1: Try to query Shopify's Storefront API for metaobjects
        // This requires implementing a GraphQL query or REST API call
        var discoveryAttempts = [
            function() {
                // Try GraphQL Storefront API
                return discoverViaStorefrontAPI(currentData, config);
            },
            function() {
                // Try REST API approach  
                return discoverViaREST(currentData, config);
            },
            function() {
                // Use current data and let cache system work incrementally
                if (DEBUG) console.log('🔄 Using incremental cache approach with available data');
                return Promise.resolve(currentData);
            }
        ];
        
        // Try each discovery method in sequence
        var attemptIndex = 0;
        function tryNext() {
            if (attemptIndex >= discoveryAttempts.length) {
                if (DEBUG) console.log('⚠️ All discovery methods failed, using available data');
                resolve(currentData);
                return;
            }
            
            discoveryAttempts[attemptIndex]().then(function(result) {
                if (result.length > currentData.length) {
                    if (DEBUG) console.log('✅ Discovery successful:', result.length, 'metaobjects found');
                    resolve(result);
                } else {
                    attemptIndex++;
                    tryNext();
                }
            }).catch(function(error) {
                if (DEBUG) console.log('❌ Discovery attempt', attemptIndex, 'failed:', error);
                attemptIndex++;
                tryNext();
            });
        }
        
        tryNext();
    });
}

function discoverViaStorefrontAPI(currentData, config) {
    return new Promise(function(resolve, reject) {
        // This would need to be implemented with proper Shopify Storefront API
        // For now, just reject to try next method
        if (DEBUG) console.log('🔄 Storefront API discovery not implemented yet');
        reject('Not implemented');
    });
}

function discoverViaREST(currentData, config) {
    return new Promise(function(resolve, reject) {
        // This would need to be implemented with Shopify REST API
        // For now, just reject to try next method
        if (DEBUG) console.log('🔄 REST API discovery not implemented yet'); 
        reject('Not implemented');
    });
}
    
    /**
     * Utility to merge AJAX data with current data
     * @param {Array} current - Current metaobjects array
     * @param {Array} additional - Additional metaobjects from AJAX
     * @returns {Array} Merged array
     */
    function mergeMetaobjectData(Arrays) {
        var merged = [];
        Arrays.forEach(function(arr) {
            merged = merged.concat(arr);
        });
        return merged;
    }
    
    /**
     * Get MTO text for a variant
     * @param {string} locale - Language locale (en, cs, de, fr)
     * @param {string|number} variantId - Variant ID to lookup
     * @returns {string} Translated MTO text or fallback
     */
    function getMtoText(locale, variantId) {
        if (!theme || !theme.mtoLookup || !theme.mtoLookup.byVariant) {
            return theme?.strings?.waitingForStock || 'Currently unavailable';
        }
        
        var localeCode = (locale || 'en').toLowerCase();
        var variantKey = String(variantId || '');
        var translations = variantKey && theme.mtoLookup.byVariant[variantKey];
        
        if (DEBUG) {
            console.log('🔍 MTO lookup:', { locale: localeCode, variant: variantKey, found: !!translations });
            if (!translations) {
                console.log('📋 Available variants:', Object.keys(theme.mtoLookup.byVariant).slice(0, 10));
            }
        }
        
        if (!translations) {
            return theme?.strings?.waitingForStock || 'Currently unavailable';
        }
        
        return translations[localeCode] || translations.en || theme?.strings?.waitingForStock || 'Currently unavailable';
    }
    
    /**
     * Clear MTO cache (useful for debugging)
     */
    function clearCache() {
        localStorage.removeItem(CACHE_KEY);
        if (theme && theme.mtoLookup) {
            theme.mtoLookup.byVariant = {};
        }
        if (DEBUG) console.log('🗑️ MTO cache cleared');
    }
    
    /**
     * Debug function to check specific variant
     * @param {string|number} variantId - Variant ID to check
     */
    function debugVariant(variantId) {
        if (!theme || !theme.mtoLookup || !theme.mtoLookup.byVariant) {
            console.log('❌ MTO lookup not initialized');
            return;
        }
        
        var variantKey = String(variantId);
        var translations = theme.mtoLookup.byVariant[variantKey];
        
        console.table([{
            variantId: variantKey,
            found: !!translations,
            translations: translations
        }]);
    }
    
    // Expose functions globally
    window.MtoCache = {
        init: initMtoCache,
        getText: getMtoText,
        clearCache: clearCache,
        debug: debugVariant
    };
    
    // Auto-initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initMtoCache);
    } else {
        initMtoCache();
    }
    
})();
